/**
 * Normalização e exclusão de `QuestionnaireResponse` por id lógico do questionário.
 * Alinha comparações entre `Questionnaire/id`, `id` só, canonical URL e variação de casing.
 */

/**
 * Normaliza id lógico ou trecho de referência FHIR para comparação estável
 * (minúsculas, último segmento após `/`, sem query string).
 */
/** Remove sufixo de versão/canonical FHIR (`|1.0.0`) do último segmento da referência. */
function stripFhirReferenceVersionSegment(segment: string): string {
  const q = segment.split("?")[0]?.trim() ?? segment;
  const pipe = q.indexOf("|");
  return pipe >= 0 ? q.slice(0, pipe).trim() : q;
}

export function normalizeQuestionnaireLogicalIdForComparison(
  refOrId: string | undefined | null,
): string | null {
  if (refOrId == null) return null;
  const t = String(refOrId).trim();
  if (!t) return null;
  let segment = t.includes("/") ? (t.split("/").pop() ?? t) : t;
  segment = stripFhirReferenceVersionSegment(segment);
  if (!segment) return null;
  return segment.toLowerCase();
}

/**
 * Extrai id lógico comparável do campo `questionnaire` de um `QuestionnaireResponse`
 * (string, Reference.reference ou vazio).
 */
export function normalizeQuestionnaireReferenceToLogicalId(
  questionnaire: fhir5.QuestionnaireResponse["questionnaire"],
): string | null {
  if (questionnaire == null) return null;
  if (typeof questionnaire === "string") {
    return normalizeQuestionnaireLogicalIdForComparison(questionnaire);
  }
  const ref = (questionnaire as { reference?: string }).reference;
  return normalizeQuestionnaireLogicalIdForComparison(ref);
}

/**
 * Id lógico extraído de `QuestionnaireResponse.questionnaire` para leitura FHIR
 * (`Questionnaire/{id}`), preservando o casing retornado pelo servidor.
 *
 * Diferente de `normalizeQuestionnaireReferenceToLogicalId`, usado só para comparação
 * (minúsculas). Aqui evitamos normalizar para não quebrar `read` em servidores
 * sensíveis a maiúsculas/minúsculas.
 */
export function questionnaireReferenceToLogicalIdRaw(
  questionnaire: fhir5.QuestionnaireResponse["questionnaire"],
): string | null {
  if (questionnaire == null) return null;
  const ref =
    typeof questionnaire === "string"
      ? questionnaire.trim()
      : String(
          (questionnaire as { reference?: string }).reference ?? "",
        ).trim();
  if (!ref) return null;
  let segment = ref.includes("/") ? (ref.split("/").pop() ?? ref) : ref;
  segment = stripFhirReferenceVersionSegment(segment);
  return segment || null;
}

/**
 * Conjunto de ids a excluir: sempre o questionário de registro clínico
 * (`VITE_QUESTIONNAIRE_ID`, o mesmo valor usado por `endpoints.QUESTIONNARIE_ID` no legado)
 * mais ids adicionais vindos do chamador (ex.: questionários da aba Dados objetivos).
 */
export function buildExcludeQuestionnaireLogicalIdSet(
  additionalLogicalIds: readonly string[] | undefined,
): Set<string> {
  const set = new Set<string>();
  const clinicalRaw = import.meta.env.VITE_QUESTIONNAIRE_ID as string | undefined;
  const clinical = normalizeQuestionnaireLogicalIdForComparison(clinicalRaw);
  if (clinical) set.add(clinical);
  for (const id of additionalLogicalIds ?? []) {
    const n = normalizeQuestionnaireLogicalIdForComparison(id);
    if (n) set.add(n);
  }
  return set;
}

export function filterQuestionnaireResponsesByExcludedQuestionnaires(
  responses: fhir5.QuestionnaireResponse[],
  excludeLogicalIds: ReadonlySet<string>,
): fhir5.QuestionnaireResponse[] {
  if (excludeLogicalIds.size === 0) return responses;
  return responses.filter((qr) => {
    const logical = normalizeQuestionnaireReferenceToLogicalId(qr.questionnaire);
    if (!logical) return true;
    return !excludeLogicalIds.has(logical);
  });
}
